/**
 * L'editor di formule (CF11) — CL04.
 *
 * Presentazione sopra `formula.ts`, che e' dove vivono le regole. Qui non si
 * interpreta niente: nessun `eval`, nessun `Function`, nessun parser — la
 * sintassi la valida il server, e questo file si limita a mostrare quello che ha
 * risposto.
 *
 * Quattro scelte che si vedono nel codice:
 *
 * - L'input mostra le label. `formula.ts` conserva i riferimenti ai fieldId
 *   durante le modifiche; il server riceve sempre la formula canonica.
 * - **Il cursore va dove l'inserimento e' finito.** `insertToken` restituisce
 *   anche la posizione: senza, ogni token inserito riporterebbe il cursore in
 *   fondo.
 * - **Nessun verde prima della risposta.** Lo stato iniziale e' "in attesa" e
 *   resta tale finche' il server non ha detto qualcosa: un verde su una formula
 *   che il client ha solo guardato sarebbe una promessa non mantenuta.
 * - **La posizione di un problema si dice, non si impone.** Il server risponde
 *   mentre l'utente sta ancora scrivendo: spostargli il cursore addosso sarebbe
 *   peggio che lasciarlo cercare, quindi la posizione si mostra accanto.
 */

import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import type { FieldDefinition, FormulaPreviewResult } from "model/CustomFields";
import { customFieldsApi } from "api/customFieldsApi";
import { choiceLabelKey } from "./controlKinds";
import { displayValue } from "./valueFormat";
import {
  availableTokens,
  displayExpression,
  displayedSelectionToCanonical,
  editDisplayedExpression,
  firstDiagnostic,
  insertToken,
  isCurrentResponse,
  nextRequestId,
  previewState,
  referencedFieldIds,
} from "./formula";

/**
 * La pausa fra un tasto e la richiesta.
 *
 * Una richiesta per carattere manderebbe al server venti validazioni mentre si
 * scrive `ROUND({...`, e nessuna delle venti e' quella che interessa. Nella
 * pausa lo stato resta "in attesa", che e' esattamente cio' che e'.
 */
const PAUSA_MS = 300;

/** I tipi risultato del contratto: gli stessi quattro che risponde l'anteprima. */
const OUTPUT_TYPES = ["text", "number", "date", "boolean"] as const;

/**
 * L'esito dell'anteprima per l'espressione corrente.
 *
 * Lo legge chi deve decidere se salvare: il client non interpreta la formula,
 * quindi l'unico "valid" che conta e' quello che ha detto il server.
 * `unavailable` e' l'unico caso senza verdetto — modello non ancora salvato —
 * e resta salvabile come prima.
 */
export type FormulaPreviewOutcome =
  | "empty"
  | "pending"
  | "valid"
  | "invalid"
  | "failed"
  | "unavailable";

interface FormulaConfigurationProps {
  /**
   * Il modello su cui il server valida.
   *
   * Assente finche' il modello non e' salvato: non c'e' niente da validare, e
   * dirlo e' meglio che mostrare un'attesa che non finira'.
   */
  modelId?: string;
  /** Gli altri campi del modello: sono i token che si possono inserire. */
  fields?: readonly FieldDefinition[];
  additionalFields?: readonly FieldDefinition[];
  /** La label del parametro, risolta dal configuratore. */
  label: string;
  value: string;
  /**
   * Il tipo del risultato come sta **adesso** nel config.
   *
   * Non si sceglie piu' — lo legge il server dalla formula — e per questo non fa
   * parte della richiesta: qui e' solo cio' che il campo ha dichiarato finora, e
   * resta a schermo finche' l'anteprima non risponde con qualcosa di piu' fresco.
   */
  outputType?: string;
  precision?: number;
  onChange: (expression: string) => void;
  /**
   * Il tipo che il server ha letto dalla formula.
   *
   * Chi possiede il config lo scrive (`config.outputType`): la dichiarazione
   * salvata e' quella che il server ha verificato, non una scelta del client.
   * Un tipo diverso da quelli del contratto non arriva: non e' un tipo.
   */
  onOutputTypeChange?: (outputType: string) => void;
  showErrors?: boolean;
  /**
   * L'esito per l'espressione corrente, appena cambia.
   *
   * Serve al configuratore per bloccare il salvataggio finche' il server non
   * ha detto `valid`: dedurre il verdetto qui in locale sarebbe un secondo
   * motore, e il client non ne ha uno.
   */
  onPreviewStateChange?: (outcome: FormulaPreviewOutcome) => void;
}

const FormulaConfiguration: React.FC<FormulaConfigurationProps> = ({
  modelId,
  fields = [],
  additionalFields,
  label,
  value,
  outputType,
  precision = 2,
  onChange,
  onOutputTypeChange,
  showErrors = true,
  onPreviewStateChange,
}) => {
  const translations = useTranslations();
  const inputRef = useRef<HTMLInputElement | null>(null);
  /** Dove rimettere il cursore dopo un inserimento, o `null`. */
  const cursorRef = useRef<number | null>(null);
  /**
   * L'ultima richiesta partita.
   *
   * L'editor manda una richiesta per ogni modifica e le risposte possono
   * arrivare fuori ordine: applicare la penultima mostrerebbe il risultato di
   * una formula che l'utente ha gia' cambiato.
   */
  const requestRef = useRef(0);
  /**
   * L'ultimo `onOutputTypeChange` ricevuto.
   *
   * La richiesta parte da un effetto, e una dipendenza che cambia a ogni render
   * (una freccia scritta dal chiamante) lo farebbe ripartire a ogni render, in
   * un giro che non finisce: il riferimento tiene la richiesta fuori da quel
   * giro.
   */
  const tipoChangeRef = useRef(onOutputTypeChange);
  useEffect(() => {
    tipoChangeRef.current = onOutputTypeChange;
  });
  const [response, setResponse] = useState<FormulaPreviewResult | null>(null);
  const [failed, setFailed] = useState(false);

  // Un modello non salvato e' l'unico motivo per non chiedere: non c'e' niente
  // da validare, e dirlo e' meglio che mostrare un'attesa che non finira'.
  const canPreview = Boolean(modelId);

  const state = previewState(value, response);

  /**
   * L'esito per l'espressione corrente, senza dedurre niente in locale.
   *
   * `valid` / `invalid` sono la risposta del server; `pending` e' l'assenza di
   * una risposta; `failed` e' un guasto di rete, che non e' una formula
   * invalida; `unavailable` e' l'unico caso in cui il server non e' stato
   * interpellato affatto.
   */
  const outcome: FormulaPreviewOutcome =
    value.trim() === ""
      ? "empty"
      : !canPreview
        ? "unavailable"
        : failed
          ? "failed"
          : state.kind === "valid"
            ? "valid"
            : state.kind === "invalid"
              ? "invalid"
              : "pending";

  // La richiesta parte da un effetto: il riferimento tiene il callback fuori
  // dalle dipendenze, cosi' una freccia scritta dal chiamante non lo fa
  // ripartire a ogni render.
  const outcomeChangeRef = useRef(onPreviewStateChange);
  useEffect(() => {
    outcomeChangeRef.current = onPreviewStateChange;
  });
  useEffect(() => {
    outcomeChangeRef.current?.(outcome);
  }, [outcome]);

  const tokens = useMemo(() => availableTokens(fields), [fields]);

  const labelFor = (fieldId: string) => fields.find((f) => f.fieldId === fieldId)?.label;
  /** La formula come la legge un progettista: `{Importo}` invece di `@{uuid}`. */
  const readable = displayExpression(value, labelFor);
  /**
   * I riferimenti a campi che nel modello non ci sono piu'.
   *
   * Restano visibili come riferimenti: e' un errore di configurazione, non un
   * testo da ripulire, e nasconderlo lo renderebbe irrintracciabile.
   */
  const orfani = useMemo(
    () => referencedFieldIds(value).filter((fieldId) => labelFor(fieldId) === undefined),
    // `labelFor` legge `fields`, che e' gia' nella lista.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, fields]
  );

  // Il tipo in config si valida contro l'elenco del contratto prima di
  // mostrarlo: e' una stringa che arriva dal modello, e a schermo ci va solo se
  // e' uno dei quattro tipi.
  const tipoDichiarato = OUTPUT_TYPES.find((tipo) => tipo === outputType);

  useEffect(() => {
    // La risposta precedente parlava di un'altra formula: non e' piu' una
    // risposta, e lo stato torna "in attesa".
    setResponse(null);
    setFailed(false);
    if (!canPreview || value.trim() === "") return;

    const id = nextRequestId(requestRef.current);
    requestRef.current = id;

    const timer = setTimeout(() => {
      customFieldsApi
        .previewFormula(modelId as string, {
          expression: value,
          precision,
          // Nessun campione: il configuratore non ha valori con cui provare, e
          // inventarne mostrerebbe il risultato di dati che non esistono. Resta
          // una validazione, che e' quello che il contratto promette.
          samples: {},
          additionalFields,
        })
        .then((result) => {
          if (!isCurrentResponse(id, requestRef.current)) return;
          setResponse(result);
          // Il tipo lo dichiara il server: e' il suo verdetto, e il config lo
          // conserva per il salvataggio.
          if (result.outputType) tipoChangeRef.current?.(result.outputType);
        })
        .catch(() => {
          // Un guasto di rete non e' una formula invalida: si dice che
          // l'anteprima non c'e' stata, e di certo non si mostra un verde.
          if (isCurrentResponse(id, requestRef.current)) setFailed(true);
        });
    }, PAUSA_MS);

    return () => clearTimeout(timer);
  }, [value, canPreview, modelId, precision, additionalFields]);

  /** Inserisce un riferimento alla posizione del cursore. */
  const insert = (fieldId: string) => {
    const input = inputRef.current;
    // Campo mai toccato: il cursore e' in fondo, che e' dove si scrive.
    const start = input?.selectionStart ?? readable.length;
    const end = input?.selectionEnd ?? start;
    const selection = displayedSelectionToCanonical(value, start, end, labelFor);
    const next = insertToken(value, selection.start, selection.end, fieldId);
    cursorRef.current = displayExpression(next.expression.slice(0, next.cursor), labelFor).length;
    onChange(next.expression);
  };

  // Il cursore si rimette dopo il render: al momento dell'inserimento il campo
  // contiene ancora l'espressione vecchia.
  useEffect(() => {
    const cursor = cursorRef.current;
    if (cursor === null) return;
    cursorRef.current = null;
    inputRef.current?.focus();
    inputRef.current?.setSelectionRange(cursor, cursor);
  });

  const problem = firstDiagnostic(state);

  /**
   * Il tipo del risultato, come lo legge il progettista.
   *
   * E' quello che il server ha risposto sull'ultima formula; prima di una
   * risposta resta quello che il campo ha gia' dichiarato, perche' e' l'unico
   * vero in quel momento. Una voce di catalogo assente non si mostra come
   * chiave: si mostra il tipo, che si legge comunque.
   */
  const tipoMostrato = response?.outputType ?? tipoDichiarato;
  const nomeTipo = (tipo: string): string => {
    const chiave = choiceLabelKey("outputType", tipo);
    const voce = chiave ? getTranslation(translations, chiave) : tipo;
    return voce === chiave ? tipo : voce;
  };

  return (
    <Stack gap={1} data-testid="cf-config-expression-editor">
      <TextField
        size="small"
        fullWidth
        label={label}
        inputRef={inputRef}
        value={readable}
        error={showErrors && state.kind === "invalid"}
        // La formula e' sintassi: il correttore del browser la segnerebbe come
        // errore ortografico a ogni funzione.
        inputProps={{ "data-testid": "cf-config-expression", spellCheck: false }}
        onChange={(e) => onChange(editDisplayedExpression(value, e.target.value, labelFor))}
        onKeyUp={(e) => e.stopPropagation()}
      />

      {showErrors && orfani.length > 0 && (
        <Alert severity="warning" data-testid="cf-config-expression-missing-reference">
          {getTranslation(translations, "custom_fields.formula.missing_reference")}
        </Alert>
      )}

      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          data-testid="cf-config-expression-fields-label"
        >
          {getTranslation(translations, "custom_fields.formula.fields.label")}
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={0.5} data-testid="cf-config-expression-tokens">
          {tokens.map((token) => (
            <Chip
              key={token.fieldId}
              size="small"
              label={token.token}
              onClick={() => insert(token.fieldId)}
              data-testid={`cf-config-expression-token-${token.fieldId}`}
            />
          ))}
        </Stack>
        {tokens.length === 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid="cf-config-expression-no-fields"
          >
            {getTranslation(translations, "custom_fields.formula.no_fields")}
          </Typography>
        )}
      </Box>

      <Box data-testid="cf-config-expression-preview">
        {!canPreview && value.trim() !== "" && (
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid="cf-config-expression-preview-unavailable"
          >
            {getTranslation(translations, "custom_fields.formula.preview_unavailable")}
          </Typography>
        )}

        {/* In attesa: e' l'unica cosa vera finche' il server non ha risposto.
            Non anche quando la richiesta e' fallita, che non e' piu' un'attesa. */}
        {canPreview && state.kind === "pending" && !failed && (
          <Stack direction="row" gap={1} alignItems="center">
            <CircularProgress size={16} data-testid="cf-config-expression-preview-pending" />
            <Typography variant="caption" color="text.secondary">
              {getTranslation(translations, "custom_fields.formula.preview_pending")}
            </Typography>
          </Stack>
        )}

        {canPreview && failed && (
          <Alert severity="warning" data-testid="cf-config-expression-preview-failed">
            {getTranslation(translations, "custom_fields.formula.preview_failed")}
          </Alert>
        )}

        {/* Il verde arriva solo da qui, cioe' solo da una risposta del server. */}
        {state.kind === "valid" && (
          <Stack direction="row" gap={1} alignItems="center">
            <Chip
              size="small"
              color="success"
              label={getTranslation(translations, "custom_fields.formula.preview_valid")}
              data-testid="cf-config-expression-preview-valid"
            />
            <Typography variant="caption" data-testid="cf-config-expression-preview-value">
              {/* `null` e' un risultato, non un guasto: si distingue dal vuoto. */}
              {state.value === null
                ? getTranslation(translations, "custom_fields.formula.value_null")
                : displayValue(state.value)}
            </Typography>
          </Stack>
        )}

        {showErrors && state.kind === "invalid" && (
          <Alert severity="error" data-testid="cf-config-expression-preview-invalid">
            <Typography variant="body2">
              {getTranslation(translations, "custom_fields.formula.invalid")}
            </Typography>
            {state.diagnostics.map((diagnostic, index) => (
              <Typography
                // La diagnostica non ha un id: codice e posizione la
                // identificano dentro questa risposta.
                key={`${diagnostic.code}-${diagnostic.tokenStart ?? index}`}
                variant="caption"
                display="block"
                data-testid="cf-config-expression-preview-diagnostic"
              >
                {getTranslation(translations, diagnostic.messageKey)}
              </Typography>
            ))}
            {problem?.tokenStart != null && (
              <Typography
                variant="caption"
                display="block"
                data-testid="cf-config-expression-preview-position"
              >
                {getTranslation(translations, "custom_fields.formula.position")}
                {": "}
                {problem.tokenStart}
              </Typography>
            )}
          </Alert>
        )}

        {/* Il tipo del risultato: lo legge il server dalla formula, e qui si
            legge quello che ha risposto. Un `select` sarebbe la scelta di
            dichiarare altro da quello che la formula dice. */}
        {tipoMostrato && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            data-testid="cf-config-outputType"
          >
            {getTranslation(translations, "custom_fields.config.outputType.label")}
            {": "}
            {nomeTipo(tipoMostrato)}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

export default FormulaConfiguration;
